import { useUpdateProject, useUpdateProjectLogo } from 'api/ProjectsAPI';
import { useCurrentProject } from 'api/queries/projects';
import { fetchUserProjectRequest } from 'core/userContext';
import {
  addNegativeToastWithErrorId,
  addPositiveToast,
  removeAllToasts,
} from 'modules/toastContainer';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ErrorHttpResponse } from 'services/api';
import { FIELD_NAMES, FIELDS_WITH_CUSTOM_API_ENDPOINTS } from '../constants';
import { ProjectSettingsFormSchemaType, useProjectSettingsQueryContext } from '../contexts';
import { useManageLabels } from '../hooks/useManageLabels';
import { getUpdateProjectSettingsPayload } from '../utils';
import noop from 'utils/noop';

const { HOSTED_UNSUBSCRIBE_URL, LOGO_URL, LABELS, SENDING_DOMAIN } = FIELD_NAMES;

/**
 * Sending Domain must have a valid TLD which is checked on the BE POST event
 * FE validation only verifies the string is structured like a domain
 * It cannot also be used in another org
 * @see https://github.com/Iterable/iterable/blob/66fef7c354bca44b85e4cb123fa11733c2dabc7e/conf/messages#L1022-L1023
 */
const sendingDomainErrors = [
  /** domains that do not use a valid top-level-domain or are otherwise malformed */
  'error.settings.malformedDNSDomain',
  /** domains that are used in another org */
  'error.settings.forbiddenSendingDomain',
];

/**
 * Hosted Unsubscribe URL must have a valid TLD which is checked on the BE POST event
 * FE validation only verifies the string is structured like a domain
 */
const hostedUnsubscribeErrors = [
  /** domains that do not use a valid top-level-domain or are otherwise malformed */
  'error.settings.malformedUnsubUrl',
];

export const useSubmitProjectSettings = () => {
  const [isLabelsComplete, setIsLabelsComplete] = useState(false);
  const [isProjectComplete, setIsProjectComplete] = useState(false);
  const [isProjectLogoComplete, setIsProjectLogoComplete] = useState(false);
  const dispatch = useDispatch();
  const { project, labels, refetchLabels, refetchProject } = useProjectSettingsQueryContext();
  const { refetch: refetchCurrentProject } = useCurrentProject();

  const {
    formState: { dirtyFields, isDirty, isSubmitSuccessful, isValid },
    getValues,
    handleSubmit,
    reset,
    resetField,
    setError,
  } = useFormContext<ProjectSettingsFormSchemaType>();

  const { handleManageLabels } = useManageLabels(() => {
    resetField(LABELS, { defaultValue: getValues(LABELS) });
    refetchLabels();
    setIsLabelsComplete(true);
  });

  const {
    isLoading: isUpdateProjectLoading,
    mutate: updateProject,
    reset: resetUpdateProject,
    mutateAsync: updateProjectAsync,
  } = useUpdateProject(project.id, {
    onSuccess: () => {
      dispatch(addPositiveToast('Project settings updated successfully'));
      setIsProjectComplete(true);
    },
    onError: (axiosError: any) => {
      const errorData = axiosError?.response?.data as ErrorHttpResponse;
      const { code, message } = errorData || {};
      /** Check for specific error to set custom error message at field level */
      if (sendingDomainErrors.includes(code)) {
        setError(SENDING_DOMAIN, {
          type: 'custom',
          message,
        });
      }

      if (hostedUnsubscribeErrors.includes(code)) {
        setError(HOSTED_UNSUBSCRIBE_URL, {
          type: 'custom',
          message,
        });
      }

      const errorMessage = `Error updating project settings. ${message || ''}`;
      dispatch(addNegativeToastWithErrorId(errorMessage, 'updateProjectSettings', axiosError));
    },
  });

  const { mutate: updateLogo, mutateAsync: updateLogoAsync } = useUpdateProjectLogo(project.id, {
    onSuccess: () => {
      dispatch(addPositiveToast('Logo updated successfully'));
    },
    onError: (error) => {
      dispatch(addNegativeToastWithErrorId('Error updating logo', 'useUpdateProjectLogo', error));
    },
  });

  /**
   * 📖 Storytime:
   * Some time actually not long at all ago, we migrated the project settings page from Angular to React,
   * and as part of this migration, we also redesigned the page. However, due to limited backend resources,
   * we could not create a new API. As a result, we are still using a monolithic endpoint
   * that accepts a large payload containing approximately 90% of the project settings, which has several downsides.
   *
   * 👀 For example:
   * - Whenever a single field is updated, the entire section's payload must be sent to the backend.
   * - While some fields have their own API endpoints (e.g., labels), they still need to be
   * saved using the same user experience (UX) flow as the monolithic fields. This means that
   * when a user clicks "Save", we may send multiple requests to the backend,
   * which is not ideal as it complicates providing reliable feedback to the user.
   *
   * 🔭 Long-term:
   * The goal for the future is to reduce the number of items saved at once and to develop a more modular API.
   * For instance, we could support better partial updates or even implement a GraphQL mutation.
   * Ideally, we would create a separate API for each section of the form,
   * complete with UX that reflects this (e.g., their own save trigger UX).
   * But for now, we are constrained by this monolithic flow.
   *
   * @param values - The form values
   */
  const onSubmit = (values: ProjectSettingsFormSchemaType) => {
    /** Remove any toasts from previous submissions */
    dispatch(removeAllToasts());

    /**
     * Given that this is such a large form, we are breaking it up into sections
     * and testing it piece by piece. Testing progress for form submission is
     * documented here. Testing progress for the initial step of form hydration
     * is documented in ../utils/defaultValues.ts.
     */

    /**
     * Logo is updated via a separate request -- only send this request if
     * logo has been replaced.
     */
    const onLogoSuccess = () => {
      setIsProjectLogoComplete(true);
    };
    if (dirtyFields[LOGO_URL]) {
      const formData = new FormData();
      formData.append('logoUrl', values[LOGO_URL] as File);
      updateLogo(formData, { onSuccess: onLogoSuccess });
    } else {
      onLogoSuccess();
    }

    /**
     * Labels
     * Pass through current form values and initial labels to handleManageLabels
     * If labels have been created/edited/deleted this will trigger the separate requests
     * If no label changes, set the labels flag to allow resetting form and fetch APIs
     */
    if (dirtyFields[LABELS]) {
      handleManageLabels(values[LABELS], labels);
    } else {
      setIsLabelsComplete(true);
    }

    /**
     * Project settings
     * Capture any additional dirty fields and update the project accordingly
     * we only want to send the request if there are other fields that have been updated.
     */
    const dirtyGeneralFields = Object.keys(dirtyFields).filter(
      (field: any) => !FIELDS_WITH_CUSTOM_API_ENDPOINTS.includes(field),
    );

    if (dirtyGeneralFields.length > 0) {
      const updatedValues = getUpdateProjectSettingsPayload(values, dirtyFields);
      updateProject(updatedValues);
    } else {
      setIsProjectComplete(true);
    }
  };
  const onSubmitWithCallback = (
    values: ProjectSettingsFormSchemaType,
    _?: React.BaseSyntheticEvent,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    // The pattern here is to create a list of functions that contain our promise backed calls to update project settings
    // Once collected the resources that must be updated are called, then we handle the result based on all success or some failure

    const resourcesToCall = [];
    dispatch(removeAllToasts());

    const onLogoSuccess = () => {
      setIsProjectLogoComplete(true);
    };
    if (dirtyFields[LOGO_URL]) {
      const formData = new FormData();
      formData.append('logoUrl', values[LOGO_URL] as File);
      resourcesToCall.push(() => updateLogoAsync(formData, { onSuccess: onLogoSuccess }));
    } else {
      onLogoSuccess();
    }

    // useManageLabels doesn't forward onError so no way to account for it without modifying the hook, hence it's left out of resourcesToCall
    if (dirtyFields[LABELS]) {
      handleManageLabels(values[LABELS], labels);
    } else {
      setIsLabelsComplete(true);
    }

    const dirtyGeneralFields = Object.keys(dirtyFields).filter(
      (field: any) => !FIELDS_WITH_CUSTOM_API_ENDPOINTS.includes(field),
    );

    if (dirtyGeneralFields.length > 0) {
      const updatedValues = getUpdateProjectSettingsPayload(values, dirtyFields);
      resourcesToCall.push(() => updateProjectAsync({ data: updatedValues }));
    } else {
      setIsProjectComplete(true);
    }

    Promise.allSettled(resourcesToCall.map((fn) => fn())).then((results) => {
      const allSuccessful = results.every((r) => r.status === 'fulfilled');
      if (allSuccessful) {
        onSuccess();
      } else {
        onError();
      }
    });
  };

  /** Reset all form state and APIs once all label operations are complete */
  useEffect(() => {
    if (isLabelsComplete && isSubmitSuccessful && isProjectComplete && isProjectLogoComplete) {
      /**
       * Fetch latest data from Project and Labels
       * Fetching currentProject triggers redux store update
       * used across the app (ie. project name in global nav)
       */
      resetUpdateProject();
      dispatch(fetchUserProjectRequest());
      refetchProject();
      refetchCurrentProject();
      refetchLabels();
      /**
       * reset form fields
       * keep submitCount to avoid multiple reset calls making the screen flash when changes are applied
       */
      reset((values) => values, { keepDirty: false, keepSubmitCount: true });
      /** reset flags for next round of updates */
      setIsLabelsComplete(false);
      setIsProjectLogoComplete(false);
      setIsProjectComplete(false);
    }
  }, [
    dispatch,
    isLabelsComplete,
    isProjectComplete,
    isProjectLogoComplete,
    isSubmitSuccessful,
    refetchCurrentProject,
    refetchProject,
    refetchLabels,
    reset,
    resetUpdateProject,
  ]);

  const handleSubmitWithCallback = (onSuccess: () => void, onError: () => void) => {
    const innerHandleSubmit = (
      values: ProjectSettingsFormSchemaType,
      e?: React.BaseSyntheticEvent,
    ) => onSubmitWithCallback(values, e, onSuccess, onError);
    return handleSubmit(innerHandleSubmit);
  };

  return {
    isDirty,
    isLoading: isUpdateProjectLoading,
    isValid,
    onSubmit: handleSubmit(onSubmit),
    onSubmitWithCallback: (onSuccess: () => void, onError: () => void) =>
      handleSubmitWithCallback(onSuccess, onError),
  };
};
